import React from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

/* =====================================================================
   ENGINEERING PHILOSOPHY — centered premium statement with very
   generous whitespace, dark industrial background and subtle orange
   ambient glow.
   ===================================================================== */
export default function EngineeringPhilosophy() {
  return (
    <section
      data-testid="about-philosophy"
      className="relative bg-liftile-matte-black py-28 md:py-40 lg:py-48 overflow-hidden"
    >
      {/* Faint blueprint grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />
      {/* Orange ambient halo behind the statement */}
      <div
        aria-hidden
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[760px] h-[760px] rounded-full pointer-events-none opacity-30"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.32) 0%, rgba(255,107,26,0) 65%)",
          filter: "blur(50px)",
        }}
      />

      <div className="container-x relative">
        <div className="max-w-4xl mx-auto text-center">
          {/* Eyebrow */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            className="inline-flex items-center gap-3 mb-10"
          >
            <span className="block w-12 h-px bg-liftile-orange" />
            <span className="font-heading text-[10px] md:text-xs uppercase tracking-[0.32em] text-liftile-orange">
              Engineering Philosophy
            </span>
            <span className="block w-12 h-px bg-liftile-orange" />
          </motion.div>

          {/* Decorative quote glyph */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            transition={{ delay: 0.08 }}
            className="flex justify-center mb-8"
            aria-hidden
          >
            <Quote className="w-10 h-10 text-liftile-orange/55" strokeWidth={1.2} />
          </motion.div>

          {/* Statement */}
          <motion.blockquote
            data-testid="philosophy-statement"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            transition={{ delay: 0.15 }}
            className="font-display italic text-liftile-cream leading-[1.18] tracking-tight text-2xl sm:text-3xl md:text-4xl lg:text-[2.6rem] space-y-6"
          >
            <p>
              &ldquo;We believe every machine represents a customer&apos;s
              hard-earned investment.
            </p>
            <p>
              That is why we never compromise on{" "}
              <span className="text-liftile-orange">
                quality, engineering or safety.
              </span>
            </p>
            <p>
              Every LIFTILE machine is built with the same care, precision and
              responsibility we would expect for ourselves.&rdquo;
            </p>
          </motion.blockquote>

          {/* Orange accent line */}
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ delay: 0.35, duration: 0.9, ease: "easeOut" }}
            style={{ transformOrigin: "center" }}
            className="mx-auto mt-14 md:mt-16 w-20 h-0.5 bg-liftile-orange"
          />

          {/* Tagline */}
          <motion.p
            data-testid="philosophy-tagline"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            transition={{ delay: 0.45 }}
            className="mt-8 font-heading uppercase tracking-[0.18em] text-base sm:text-lg md:text-xl text-liftile-cream"
          >
            Built with Integrity.{" "}
            <span className="text-liftile-orange">Delivered with Confidence.</span>
          </motion.p>
        </div>
      </div>
    </section>
  );
}
